import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import {
  Card,
  CardHeader,
  CardBody,
  CardTitle,
  Row,
  Col,
  Table,
  Badge,
  Button,
  Spinner,
  Modal,
  Form,
  Alert,
} from "react-bootstrap";
import axios from "axios";
import { toast } from "react-toastify";
import { getUserFromLocalStorage } from "../../utilities/user";
import socket from "../../utilities/socket";
import RiderCredentialsModal from "../../components/RiderCredentialsModal";
import { colors, font } from "./partnerTheme";
import AppSelect from "../../components/ui/AppSelect";

const API_URL = process.env.REACT_APP_SERVER_URL || "";

const PartnerRiders = () => {
  const { agent } = useSelector((state) => state.auth);
  const user = agent || getUserFromLocalStorage();
  const partnerId = user?.result?.partnerId || user?.result?._id;
  const token = user?.token;

  const [riders, setRiders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [showCredentialsModal, setShowCredentialsModal] = useState(false);
  const [createdCredentials, setCreatedCredentials] = useState(null);
  const [editingRider, setEditingRider] = useState(null);
  const [saving, setSaving] = useState(false);
  const [statusUpdatingId, setStatusUpdatingId] = useState(null);
  const [formErrors, setFormErrors] = useState({ email: "", mobile: "" });
  const [filter, setFilter] = useState("all");
  const [showRiderId, setShowRiderId] = useState(false);
  const [, setTick] = useState(0); // increments every 60s to re-render live "Xm ago" labels

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    mobile: "",
    email: "",
    nationalId: "",
    vehicleType: "motorcycle",
    vehiclePlateNumber: "",
    password: "", // Optional - if empty, temp password is generated
  });

  useEffect(() => {
    if (partnerId && token) {
      fetchRiders();
    } else {
      setLoading(false);
      setError("Partner ID not found");
    }
  }, [partnerId, token]);

  // Tick every 60s so "Xm ago" labels update without a network refetch
  useEffect(() => {
    const interval = setInterval(() => setTick((t) => t + 1), 60000);
    return () => clearInterval(interval);
  }, []);

  // Socket listener for real-time rider status updates
  useEffect(() => {
    if (!partnerId) return;

    // Join partner room for real-time updates
    socket.emit("join3PLPartnerRoom", partnerId);

    // Listen for rider status changes (partner-specific event)
    const handleRiderStatusChange = ({ riderId, status, isAvailable }) => {
      setRiders((prevRiders) =>
        prevRiders.map((rider) =>
          rider._id === riderId
            ? {
                ...rider,
                onlineStatus: status,
                availability: { ...rider.availability, isAvailable },
              }
            : rider,
        ),
      );
    };

    // Listen for global rider status updates (fallback/backward compatibility)
    const handleGlobalRiderStatusUpdate = ({ riderId, status }) => {
      setRiders((prevRiders) =>
        prevRiders.map((rider) =>
          rider._id === riderId
            ? {
                ...rider,
                onlineStatus: status,
                availability: {
                  ...rider.availability,
                  isAvailable: status === "Online",
                },
              }
            : rider,
        ),
      );
    };

    // Update lastActiveAt locally when a heartbeat is received so the
    // client-side "Xm ago" counter resets to "Just now" on each heartbeat
    const handleHeartbeatUpdate = ({ riderId, lastActiveAt }) => {
      setRiders((prevRiders) =>
        prevRiders.map((rider) =>
          rider._id === riderId ? { ...rider, lastActiveAt } : rider,
        ),
      );
    };

    socket.on("rider:statusChanged", handleRiderStatusChange);
    socket.on("riderStatusUpdate", handleGlobalRiderStatusUpdate);
    socket.on("rider:heartbeatUpdate", handleHeartbeatUpdate);

    // Re-sync on socket reconnection (e.g. tab wakeup, network blip)
    const handleReconnect = () => {
      socket.emit("join3PLPartnerRoom", partnerId);
      if (isMounted) fetchRiders();
    };
    socket.on("connect", handleReconnect);

    let isMounted = true;

    // Cleanup on unmount
    return () => {
      isMounted = false;
      socket.off("rider:statusChanged", handleRiderStatusChange);
      socket.off("riderStatusUpdate", handleGlobalRiderStatusUpdate);
      socket.off("rider:heartbeatUpdate", handleHeartbeatUpdate);
      socket.off("connect", handleReconnect);
      socket.emit("leave3PLPartnerRoom", partnerId);
    };
  }, [partnerId]);

  const fetchRiders = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${API_URL}/api/3pl-partners/${partnerId}/riders`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      if (response.data.success) {
        setRiders(response.data.data?.riders || []);
      }
    } catch (err) {
      console.error("Error fetching riders:", err);
      setError("Failed to load riders");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (rider = null) => {
    if (rider) {
      setEditingRider(rider);
      setFormData({
        firstName: rider.firstName || "",
        lastName: rider.lastName || "",
        mobile: String(rider.mobile || rider.phoneNumber || ""),
        email: rider.email || "",
        nationalId: rider.nationalId || rider.licenseDetails?.nationalId || "",
        vehicleType: (
          rider.carrierType ||
          rider.vehicleDetails?.vehicleType ||
          "Motorcycle"
        ).toLowerCase(),
        vehiclePlateNumber: rider.vehicleDetails?.plateNumber || "",
        password: "", // Don't populate password
      });
    } else {
      setEditingRider(null);
      setFormData({
        firstName: "",
        lastName: "",
        mobile: "",
        email: "",
        nationalId: "",
        vehicleType: "motorcycle",
        vehiclePlateNumber: "",
      });
    }
    setFormErrors({ email: "", mobile: "" });
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingRider(null);
    setFormErrors({ email: "", mobile: "" });
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // Phone: allow digits and a leading + only
  const handlePhoneChange = (e) => {
    const cleaned = e.target.value
      .replace(/[^\d+]/g, "") // strip non-digits / non-+
      .replace(/(?!^)\+/g, ""); // keep + only at the very start
    setFormData((prev) => ({ ...prev, mobile: cleaned }));
  };

  // Email: live format validation
  const handleEmailChange = (e) => {
    const val = e.target.value;
    setFormData((prev) => ({ ...prev, email: val }));
    if (!val.trim()) {
      setFormErrors((prev) => ({ ...prev, email: "" }));
      return;
    }
    const valid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val.trim());
    setFormErrors((prev) => ({
      ...prev,
      email: valid ? "" : "Please enter a valid email address",
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const riderData = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        phone: formData.mobile, // Backend expects 'phone' or 'phoneNumber'
        phoneNumber: formData.mobile, // Also include phoneNumber for compatibility
        email: formData.email,
        nationalId: formData.nationalId,
        partnerId: partnerId,
        vehicleType: formData.vehicleType, // Backend expects vehicleType at top level
        vehicleDetails: {
          vehicleType: formData.vehicleType,
          plateNumber: formData.vehiclePlateNumber,
        },
      };

      if (editingRider) {
        // Update existing rider
        await axios.put(
          `${API_URL}/riders/${editingRider._id}/update-by-admin`,
          riderData,
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );
        toast.success("Rider updated successfully");
        handleCloseModal();
      } else {
        // Create new rider - POST /riders/3pl with partnerId in body
        const response = await axios.post(
          `${API_URL}/riders/3pl`,
          {
            ...riderData,
            password: formData.password || undefined, // Send password if provided
          },
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );

        // Show credentials if temp password was generated
        if (response.data?.data?.credentials?.tempPassword) {
          setCreatedCredentials({
            name: `${formData.firstName} ${formData.lastName}`,
            phone: formData.mobile,
            email: formData.email,
            tempPassword: response.data.data.credentials.tempPassword,
          });
          setShowCredentialsModal(true);
        }
        toast.success(response.data?.message || "Rider created successfully");
        handleCloseModal();
      }

      fetchRiders();
    } catch (err) {
      console.error("Error saving rider:", err);
      toast.error(
        err.response?.data?.error?.message ||
          err.response?.data?.message ||
          "Failed to save rider",
      );
    } finally {
      setSaving(false);
    }
  };

  const handleToggleStatus = async (rider) => {
    setStatusUpdatingId(rider._id);
    try {
      const newStatus = rider.activeStatus === "Active" ? "Inactive" : "Active";
      await axios.put(
        `${API_URL}/riders/${rider._id}/update-by-admin`,
        {
          activeStatus: newStatus,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      toast.success(
        `Rider ${newStatus === "Active" ? "activated" : "deactivated"}`,
      );
      fetchRiders();
    } catch (err) {
      toast.error(
        err.response?.data?.error?.message ||
          err.response?.data?.message ||
          "Failed to update rider status",
      );
    } finally {
      setStatusUpdatingId(null);
    }
  };

  const filteredRiders = riders.filter((rider) => {
    if (filter === "all") return true;
    if (filter === "active") return rider.activeStatus === "Active";
    if (filter === "inactive") return rider.activeStatus !== "Active";
    if (filter === "online") return rider.onlineStatus === "Online";
    return true;
  });

  const getStatusBadge = (rider) => {
    if (rider.activeStatus !== "Active") {
      return <Badge bg="secondary">Inactive</Badge>;
    }
    if (rider.onlineStatus === "Online") {
      // Compute live from raw timestamp so the label ticks every minute
      const liveMinutesAgo = rider.lastActiveAt
        ? Math.floor(
            (Date.now() - new Date(rider.lastActiveAt).getTime()) / 60000,
          )
        : null;
      const liveReachability =
        liveMinutesAgo === null
          ? "unknown"
          : liveMinutesAgo <= 2
          ? "connected"
          : liveMinutesAgo <= 10
          ? "recent"
          : "idle";
      const liveLabel =
        liveMinutesAgo === null
          ? "no heartbeat"
          : liveMinutesAgo === 0
          ? "Just now"
          : `${liveMinutesAgo}m ago`;
      const reachabilityDot = {
        connected: { color: "#01cf4d" },
        recent: { color: "#facc15" },
        idle: { color: "#facc15" },
        unknown: { color: "#9ca3af" },
      };
      const dot = reachabilityDot[liveReachability] || reachabilityDot.unknown;
      return (
        <span className="d-flex align-items-center gap-1">
          <Badge
            bg={null}
            style={{
              backgroundColor: "#32ee0c",
              color: "#041a01",
              padding: "6px 10px",
              borderRadius: "12px",
            }}
          >
            Online
          </Badge>
          <span
            style={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              backgroundColor: dot.color,
              display: "inline-block",
              border: "1px solid rgba(0,0,0,0.1)",
              flexShrink: 0,
            }}
          />
          {liveLabel && (
            <span
              style={{ fontSize: 10, color: "#6b7280", whiteSpace: "nowrap" }}
            >
              {liveLabel}
            </span>
          )}
        </span>
      );
    }
    return <Badge bg="warning">Offline</Badge>;
  };

  const getVehicleColor = (vehicleType) => {
    const normalizedType = (vehicleType || "").toLowerCase();
    const typeColors = {
      bicycle: "#f5a30bff",
      motorcycle: "#3b82f6",
      car: "#8b5cf6",
      van: "#10b981",
      truck: "#ef4444",
    };
    return typeColors[normalizedType] || colors.blue;
  };

  const getVehicleIcon = (vehicleType) => {
    const normalizedType = (vehicleType || "").toLowerCase();
    const icons = {
      bicycle: "🚲",
      motorcycle: "🏍️",
      car: "🚗",
      van: "🚐",
      truck: "🚚",
    };
    return icons[normalizedType] || "🏍️";
  };

  const isFormValid =
    formData.firstName.trim() &&
    formData.lastName.trim() &&
    String(formData.mobile || "").trim() &&
    !formErrors.email; // format check only — email is optional

  if (loading) {
    return (
      <div
        className="content d-flex justify-content-center align-items-center"
        style={{ minHeight: "400px" }}
      >
        <Spinner
          animation="grow"
          style={{ color: "rgba(127, 40, 19, 0.84)" }}
        />
      </div>
    );
  }

  return (
    <div className="content" style={{ marginTop: "-50px" }}>
      <Row className="mb-4">
        <Col>
          <h4 style={{ color: colors.body }}>
            <i className="nc-icon nc-delivery-fast me-2" />
            My Delivery Agents (DAs)
          </h4>
          <p style={{ color: colors.muted }}>Manage your delivery fleet</p>
        </Col>
        <Col className="text-end">
          <Button
            style={{ backgroundColor: colors.blue, borderColor: colors.blue }}
            onClick={() => handleOpenModal()}
          >
            <i className="nc-icon nc-simple-add me-2" />
            Add Rider
          </Button>
        </Col>
      </Row>

      {error && <Alert variant="danger">{error}</Alert>}

      {/* Stats Cards */}
      <Row className="mb-4">
        <Col md="3" sm="6">
          <Card className="card-stats">
            <CardBody>
              <Row>
                <Col xs="5">
                  <div
                    className="icon-big text-center"
                    style={{ color: colors.blue }}
                  >
                    <i className="nc-icon nc-single-02" />
                  </div>
                </Col>
                <Col xs="7">
                  <div className="numbers">
                    <p className="card-category">Total Riders</p>
                    <CardTitle tag="h4">{riders.length}</CardTitle>
                  </div>
                </Col>
              </Row>
            </CardBody>
          </Card>
        </Col>
        <Col md="3" sm="6">
          <Card className="card-stats">
            <CardBody>
              <Row>
                <Col xs="5">
                  <div
                    className="icon-big text-center"
                    style={{ color: colors.green }}
                  >
                    <i className="nc-icon nc-check-2" />
                  </div>
                </Col>
                <Col xs="7">
                  <div className="numbers">
                    <p className="card-category">Online Now</p>
                    <CardTitle tag="h4">
                      {riders.filter((r) => r.onlineStatus === "Online").length}
                    </CardTitle>
                  </div>
                </Col>
              </Row>
            </CardBody>
          </Card>
        </Col>
        <Col md="3" sm="6">
          <Card className="card-stats">
            <CardBody>
              <Row>
                <Col xs="5">
                  <div
                    className="icon-big text-center"
                    style={{ color: colors.amber }}
                  >
                    <i className="nc-icon nc-time-alarm" />
                  </div>
                </Col>
                <Col xs="7">
                  <div className="numbers">
                    <p className="card-category">Active</p>
                    <CardTitle tag="h4">
                      {riders.filter((r) => r.activeStatus === "Active").length}
                    </CardTitle>
                  </div>
                </Col>
              </Row>
            </CardBody>
          </Card>
        </Col>
        <Col md="3" sm="6">
          <Card className="card-stats">
            <CardBody>
              <Row>
                <Col xs="5">
                  <div
                    className="icon-big text-center"
                    style={{ color: colors.red }}
                  >
                    <i className="nc-icon nc-simple-remove" />
                  </div>
                </Col>
                <Col xs="7">
                  <div className="numbers">
                    <p className="card-category">Inactive</p>
                    <CardTitle tag="h4">
                      {riders.filter((r) => r.activeStatus !== "Active").length}
                    </CardTitle>
                  </div>
                </Col>
              </Row>
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Filter Buttons */}
      <Row className="mb-3">
        <Col>
          <Button
            variant={filter === "all" ? "primary" : "outline-primary"}
            size="sm"
            className="me-2"
            onClick={() => setFilter("all")}
          >
            All ({riders.length})
          </Button>
          <Button
            variant={filter === "online" ? "success" : "outline-success"}
            size="sm"
            className="me-2"
            onClick={() => setFilter("online")}
          >
            Online ({riders.filter((r) => r.onlineStatus === "Online").length})
          </Button>
          <Button
            variant={filter === "active" ? "warning" : "outline-warning"}
            size="sm"
            className="me-2"
            onClick={() => setFilter("active")}
          >
            Active ({riders.filter((r) => r.activeStatus === "Active").length})
          </Button>
          <Button
            variant={filter === "inactive" ? "secondary" : "outline-secondary"}
            size="sm"
            onClick={() => setFilter("inactive")}
          >
            Inactive ({riders.filter((r) => r.activeStatus !== "Active").length}
            )
          </Button>
        </Col>
      </Row>

      {/* Riders Table */}
      <Card>
        <CardHeader>
          <CardTitle tag="h5" style={{ color: colors.body }}>
            Rider List ({filteredRiders.length})
          </CardTitle>
        </CardHeader>
        <CardBody>
          {filteredRiders.length === 0 ? (
            <div className="text-center py-5">
              <i
                className="nc-icon nc-single-02"
                style={{ fontSize: "48px", color: colors.line }}
              />
              <p className="mt-3" style={{ color: colors.muted }}>
                No riders found. Click "Add Rider" to get started.
              </p>
            </div>
          ) : (
            <Table responsive hover>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>
                    Rider ID{" "}
                    <i
                      className={`nc-icon ${
                        showRiderId ? "nc-glasses-2" : "nc-key-25"
                      }`}
                      style={{
                        cursor: "pointer",
                        marginLeft: "4px",
                        fontSize: "0.9rem",
                      }}
                      onClick={() => setShowRiderId(!showRiderId)}
                      title={showRiderId ? "Show short ID" : "Show full ID"}
                    />
                  </th>
                  <th>Phone</th>
                  <th>Vehicle</th>
                  <th>Status</th>
                  <th>Deliveries</th>
                  <th>Rating</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredRiders.map((rider) => (
                  <tr key={rider._id}>
                    <td>
                      <strong>
                        {rider.firstName} {rider.lastName}
                      </strong>
                      <br />
                      <small style={{ color: colors.muted }}>
                        {rider.email}
                      </small>
                    </td>
                    <td>
                      {showRiderId ? (
                        <code
                          style={{
                            fontSize: "0.75rem",
                            backgroundColor: colors.lineSoft,
                            padding: "2px 6px",
                            borderRadius: "4px",
                            color: colors.body,
                            userSelect: "all",
                          }}
                        >
                          {rider._id}
                        </code>
                      ) : (
                        <span
                          style={{
                            fontSize: "0.78rem",
                            fontFamily: font.mono,
                            backgroundColor: colors.lineSoft,
                            padding: "2px 7px",
                            borderRadius: "4px",
                            color: colors.muted,
                            letterSpacing: "0.03em",
                          }}
                          title={rider._id}
                        >
                          #{rider._id?.slice(-4)}
                        </span>
                      )}
                    </td>
                    <td>{rider.mobile || rider.phoneNumber}</td>
                    <td>
                      <Badge
                        bg=""
                        style={{
                          backgroundColor: getVehicleColor(rider.carrierType),
                          color: "#fff",
                          padding: "8px 12px",
                          fontSize: "1.7rem",
                          lineHeight: 1,
                        }}
                        title={
                          rider.carrierType ||
                          rider.vehicleDetails?.vehicleType ||
                          "Motorcycle"
                        }
                      >
                        {getVehicleIcon(
                          rider.carrierType ||
                            rider.vehicleDetails?.vehicleType,
                        )}
                      </Badge>
                      <br />
                      <small>{rider.vehicleDetails?.plateNumber}</small>
                    </td>
                    <td>{getStatusBadge(rider)}</td>
                    <td>{rider.performanceMetrics?.totalDeliveries || 0}</td>
                    <td>
                      {rider.performanceMetrics?.averageRating
                        ? `${rider.performanceMetrics.averageRating.toFixed(
                            1,
                          )} ⭐`
                        : "N/A"}
                    </td>
                    <td>
                      <Button
                        variant="outline-primary"
                        size="sm"
                        className="me-1"
                        onClick={() => handleOpenModal(rider)}
                      >
                        <i className="nc-icon nc-settings-gear-64" />
                      </Button>
                      <Button
                        variant={null}
                        size="sm"
                        onClick={() => handleToggleStatus(rider)}
                        disabled={statusUpdatingId === rider._id}
                        style={{
                          backgroundColor:
                            rider.activeStatus === "Active"
                              ? "#dc2626"
                              : "#16a34a",
                          borderColor:
                            rider.activeStatus === "Active"
                              ? "#dc2626"
                              : "#16a34a",
                          color: "#ffffff",
                        }}
                      >
                        {statusUpdatingId === rider._id ? (
                          <>
                            <Spinner
                              size="sm"
                              animation="border"
                              className="me-2"
                            />
                            Updating...
                          </>
                        ) : rider.activeStatus === "Active" ? (
                          "Deactivate"
                        ) : (
                          "Activate"
                        )}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </CardBody>
      </Card>

      {/* Add/Edit Rider Modal */}
      <Modal show={showModal} onHide={handleCloseModal} size="lg">
        <Modal.Header
          closeButton
          closeVariant="white"
          style={{ backgroundColor: colors.blue, color: colors.white }}
        >
          <Modal.Title>
            {editingRider ? "Edit Rider" : "Add New Rider"}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <Row>
              <Col md="6">
                <Form.Group className="mb-3">
                  <Form.Label>First Name *</Form.Label>
                  <Form.Control
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md="6">
                <Form.Group className="mb-3">
                  <Form.Label>Last Name *</Form.Label>
                  <Form.Control
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    required
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md="6">
                <Form.Group className="mb-3">
                  <Form.Label>Phone Number *</Form.Label>
                  <Form.Control
                    type="tel"
                    name="mobile"
                    value={formData.mobile}
                    onChange={handlePhoneChange}
                    placeholder="+256..."
                    inputMode="numeric"
                    required
                  />
                  <Form.Text
                    className="text-muted"
                    style={{ fontSize: "0.75rem" }}
                  >
                    Digits only (e.g. +256700123456)
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col md="6">
                <Form.Group className="mb-3">
                  <Form.Label>Email</Form.Label>
                  <Form.Control
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleEmailChange}
                    isInvalid={!!formErrors.email}
                  />
                  {formErrors.email && (
                    <Form.Control.Feedback type="invalid">
                      {formErrors.email}
                    </Form.Control.Feedback>
                  )}
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md="6">
                <Form.Group className="mb-3">
                  <Form.Label>National ID</Form.Label>
                  <Form.Control
                    type="text"
                    name="nationalId"
                    value={formData.nationalId}
                    onChange={handleChange}
                  />
                </Form.Group>
              </Col>
              <Col md="6">
                <Form.Group className="mb-3">
                  <Form.Label>Vehicle Type</Form.Label>
                  <AppSelect block
                    name="vehicleType"
                    value={formData.vehicleType}
                    onChange={handleChange}
                  >
                    <option value="motorcycle">Motorcycle</option>
                    <option value="bicycle">Bicycle</option>
                    <option value="car">Car</option>
                    <option value="van">Van</option>
                    <option value="truck">Truck</option>
                  </AppSelect>
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md="6">
                <Form.Group className="mb-3">
                  <Form.Label>Vehicle Plate (Unique ID)</Form.Label>
                  <Form.Control
                    type="text"
                    name="vehiclePlateNumber"
                    value={formData.vehiclePlateNumber}
                    onChange={handleChange}
                    placeholder="UAA 123B"
                  />
                </Form.Group>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseModal}>
              Cancel
            </Button>
            <span
              style={{
                display: "inline-block",
                cursor: saving || !isFormValid ? "not-allowed" : "default",
              }}
              title={
                saving
                  ? "Saving…"
                  : !isFormValid
                  ? "Please fill in all required fields (First Name, Last Name, Phone)"
                  : ""
              }
            >
              <Button
                type="submit"
                style={{
                  backgroundColor: colors.blue,
                  borderColor: colors.blue,
                  pointerEvents: saving || !isFormValid ? "none" : "auto",
                }}
                disabled={saving || !isFormValid}
              >
                {saving ? (
                  <>
                    <Spinner size="sm" animation="border" className="me-2" />
                    Saving...
                  </>
                ) : editingRider ? (
                  "Update Rider"
                ) : (
                  "Add Rider"
                )}
              </Button>
            </span>
          </Modal.Footer>
        </Form>
      </Modal>

      <RiderCredentialsModal
        show={showCredentialsModal}
        onHide={() => setShowCredentialsModal(false)}
        credentials={createdCredentials}
      />
    </div>
  );
};

export default PartnerRiders;
